import type { FC, ReactNode, Ref } from "react";

import { isValidElement } from "react";

import { cx, sortCx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";

const iconsSizes = {
	lg: "*:data-icon:size-6",
	md: "*:data-icon:size-5",
	sm: "*:data-icon:size-4",
	xl: "*:data-icon:size-7",
};

// Solid event-severity backgrounds, intended for use with the `dark` theme
// (which supplies the white glyph + border overlay).
const severityColors = {
	"severity-critical": "bg-utility-severity-critical-600",
	"severity-high": "bg-utility-severity-high-400",
	"severity-low": "bg-utility-severity-low-600",
	"severity-moderate": "bg-utility-severity-moderate-500",
};

type BaseColor =
	| "brand"
	| "caution"
	| "error"
	| "gray"
	| "pink"
	| "success"
	| "warning";

type SeverityColor = keyof typeof severityColors;

const isSeverityColor = (value: string): value is SeverityColor =>
	value in severityColors;

const styles = sortCx({
	dark: {
		base: "text-fg-white shadow-xs-skeuomorphic before:absolute before:inset-px before:border before:border-white/12 before:mask-b-from-0%",
		colors: {
			brand: "bg-brand-solid before:border-utility-brand-200/12",
			caution: "bg-brand-solid before:border-utility-brand-200/12",
			error: "bg-utility-red-450 before:border-utility-red-200/12",
			gray: "bg-secondary-solid before:border-utility-neutral-200/12",
			pink: "bg-pink-solid before:border-utility-pink-200/12",
			success: "bg-success-solid before:border-utility-green-200/12",
			warning: "bg-utility-warning-500 before:border-utility-warning-200/12",
		},
		sizes: {
			lg: "size-12 rounded-[10px] before:rounded-[9px]",
			md: "size-10 rounded-lg before:rounded-[7px]",
			sm: "size-8 rounded-md before:rounded-[5px]",
			xl: "size-14 rounded-xl before:rounded-[11px]",
		},
	},

	gradient: {
		base: "rounded-full text-fg-white before:absolute before:inset-0 before:size-full before:rounded-full before:border before:mask-b-from-0% after:absolute after:block after:rounded-full",
		colors: {
			brand:
				"before:border-utility-brand-200 before:bg-utility-brand-50 after:bg-brand-solid",
			caution:
				"before:border-utility-brand-200 before:bg-utility-brand-50 after:bg-brand-solid",
			error:
				"before:border-utility-red-200 before:bg-utility-red-50 after:bg-error-solid",
			gray: "before:border-utility-neutral-200 before:bg-utility-neutral-50 after:bg-secondary-solid",
			pink: "before:border-utility-pink-200 before:bg-utility-pink-50 after:bg-pink-solid",
			success:
				"before:border-utility-green-200 before:bg-utility-green-50 after:bg-success-solid",
			warning:
				"before:border-utility-yellow-200 before:bg-utility-yellow-50 after:bg-warning-solid",
		},
		sizes: {
			lg: "size-12 after:size-8 *:data-icon:size-5",
			md: "size-10 after:size-7 *:data-icon:size-4",
			sm: "size-8 after:size-6 *:data-icon:size-4",
			xl: "size-14 after:size-10 *:data-icon:size-5",
		},
	},

	light: {
		base: "rounded-full",
		colors: {
			brand: "bg-brand-secondary text-featured-icon-light-fg-brand",
			caution: "bg-brand-secondary text-featured-icon-light-fg-brand",
			error: "bg-error-secondary text-featured-icon-light-fg-error",
			gray: "bg-tertiary text-featured-icon-light-fg-gray",
			pink: "bg-utility-pink-50 text-utility-pink-700 ring-utility-pink-200",
			success: "bg-success-secondary text-featured-icon-light-fg-success",
			warning: "bg-warning-secondary text-featured-icon-light-fg-warning",
		},
		sizes: {
			lg: "size-12",
			md: "size-10",
			sm: "size-8",
			xl: "size-14",
		},
	},

	modern: {
		base: "bg-primary shadow-xs-skeuomorphic ring-1 ring-inset",
		colors: {
			brand: "",
			caution: "",
			error: "",
			gray: "text-fg-secondary ring-primary",
			pink: "",
			success: "",
			warning: "",
		},
		sizes: {
			lg: "size-12 rounded-[10px]",
			md: "size-10 rounded-lg",
			sm: "size-8 rounded-md",
			xl: "size-14 rounded-xl",
		},
	},
	"modern-neue": {
		base: [
			"bg-primary_alt ring-1 ring-inset before:absolute before:inset-1",
			// Shadow
			"before:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1),0px_3px_3px_0px_rgba(0,0,0,0.09),1px_8px_5px_0px_rgba(0,0,0,0.05),2px_21px_6px_0px_rgba(0,0,0,0),0px_0px_0px_1px_rgba(0,0,0,0.08),1px_13px_5px_0px_rgba(0,0,0,0.01),0px_-2px_2px_0px_rgba(0,0,0,0.13)_inset] before:ring-1 before:ring-secondary_alt",
		].join(" "),
		colors: {
			brand: "",
			caution: "",
			error: "",
			gray: "text-fg-secondary ring-primary",
			pink: "",
			success: "",
			warning: "",
		},
		sizes: {
			lg: "size-12 rounded-[12px] before:rounded-[8px]",
			md: "size-10 rounded-[10px] before:rounded-[6px]",
			sm: "size-8 rounded-[8px] before:rounded-[4px]",
			xl: "size-14 rounded-[14px] before:rounded-[10px]",
		},
	},

	outline: {
		base: "before:absolute before:rounded-full before:border-2 after:absolute after:rounded-full after:border-2",
		colors: {
			brand:
				"text-fg-brand-primary before:border-fg-brand-primary/30 after:border-fg-brand-primary/10",
			caution:
				"text-fg-brand-primary before:border-fg-brand-primary/30 after:border-fg-brand-primary/10",
			error:
				"text-utility-red-450 before:border-utility-red-450/30 after:border-utility-red-450/10",
			gray: "text-fg-tertiary before:border-fg-tertiary/30 after:border-fg-tertiary/10",
			pink: "text-fg-pink-primary before:border-fg-pink-primary/30 after:border-fg-pink-primary/10",
			success:
				"text-fg-success-primary before:border-fg-success-primary/30 after:border-fg-success-primary/10",
			warning:
				"text-fg-warning-primary before:border-fg-warning-primary/30 after:border-fg-warning-primary/10",
		},
		sizes: {
			lg: "size-6 before:size-8 after:size-10.5",
			md: "size-5 before:size-7 after:size-9.5",
			sm: "size-4 before:size-6 after:size-8.5",
			xl: "size-7 before:size-9 after:size-11.5",
		},
	},
});

export interface FeaturedIconProps {
	children?: ReactNode;
	className?: string;
	color: BaseColor | SeverityColor;
	icon?: FC<{ className?: string }> | ReactNode;
	ref?: Ref<HTMLDivElement>;
	size?: "lg" | "md" | "sm" | "xl";
	theme?: "dark" | "gradient" | "light" | "modern" | "modern-neue" | "outline";
}

export const FeaturedIcon = (props: FeaturedIconProps) => {
	const {
		color = "brand",
		icon: Icon,
		size = "sm",
		theme: variant = "light",
		...otherProps
	} = props;

	return (
		<div
			{...otherProps}
			className={cx(
				"relative flex shrink-0 items-center justify-center",

				iconsSizes[size],
				styles[variant].base,
				styles[variant].sizes[size],
				isSeverityColor(color)
					? severityColors[color]
					: styles[variant].colors[color],

				props.className,
			)}
			data-featured-icon
		>
			{isReactComponent(Icon) && <Icon className="z-1" data-icon />}
			{isValidElement(Icon) && <div className="z-1">{Icon}</div>}

			{props.children}
		</div>
	);
};
